import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CatalogCategory } from "../lib/catalog";

type CatalogFiltersProps = {
  basePath: "/shop" | "/shop/search";
  categories: CatalogCategory[];
  initialQuery?: string;
  initialCategory?: string;
  initialMin?: string;
  initialMax?: string;
  initialSort?: string;
  initialInStock?: boolean;
};

export function CatalogFilters({
  basePath,
  categories,
  initialQuery = "",
  initialCategory = "",
  initialMin = "",
  initialMax = "",
  initialSort = "featured",
  initialInStock = false
}: CatalogFiltersProps) {
  const navigate = useNavigate();
  const lastAutoAppliedQuery = useRef(initialQuery.trim());
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState(initialCategory);
  const [min, setMin] = useState(initialMin);
  const [max, setMax] = useState(initialMax);
  const [sort, setSort] = useState(initialSort);
  const [inStock, setInStock] = useState(initialInStock);
  const mobileFiltersId = `${basePath.replace(/\//g, "")}-mobile-filters`;

  function buildPath(nextQuery: string) {
    const params = new URLSearchParams();

    if (nextQuery.trim()) params.set("q", nextQuery.trim());
    if (category) params.set("category", category);
    if (min) params.set("min", min);
    if (max) params.set("max", max);
    if (sort) params.set("sort", sort);
    if (inStock) params.set("in_stock", "true");

    return `${basePath}${params.toString() ? `?${params.toString()}` : ""}`;
  }

  useEffect(() => {
    lastAutoAppliedQuery.current = initialQuery.trim();
    setQuery(initialQuery);
    setCategory(initialCategory);
    setMin(initialMin);
    setMax(initialMax);
    setSort(initialSort);
    setInStock(initialInStock);
  }, [initialCategory, initialInStock, initialMax, initialMin, initialQuery, initialSort]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const initialNormalizedQuery = initialQuery.trim();

    if (
      normalizedQuery === initialNormalizedQuery ||
      normalizedQuery === lastAutoAppliedQuery.current
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      lastAutoAppliedQuery.current = normalizedQuery;
      navigate(buildPath(query), { replace: true });
    }, 650);

    return () => {
      window.clearTimeout(timer);
    };
  }, [initialQuery, navigate, query]);

  function submit(event: FormEvent) {
    event.preventDefault();
    navigate(buildPath(query));
  }

  function reset() {
    setQuery("");
    setCategory("");
    setMin("");
    setMax("");
    setSort("featured");
    setInStock(false);
    navigate(basePath);
  }

  const filterControls = (
    <>
      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
          Category
        </label>
        <select
          className="field-control px-4 py-3 text-sm"
          onChange={(event) => setCategory(event.target.value)}
          value={category}
        >
          <option value="">All categories</option>
          {categories.map((item) => (
            <option key={item.id} value={item.name}>
              {item.name} ({item.productCount})
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
            Min KES
          </label>
          <input
            className="field-control px-4 py-3 text-sm"
            inputMode="numeric"
            onChange={(event) => setMin(event.target.value)}
            placeholder="0"
            value={min}
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
            Max KES
          </label>
          <input
            className="field-control px-4 py-3 text-sm"
            inputMode="numeric"
            onChange={(event) => setMax(event.target.value)}
            placeholder="5000"
            value={max}
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
          Sort
        </label>
        <select
          className="field-control px-4 py-3 text-sm"
          onChange={(event) => setSort(event.target.value)}
          value={sort}
        >
          <option value="featured">Featured</option>
          <option value="popular">Popular</option>
          <option value="newest">Newest</option>
          <option value="price_asc">Price: Low to high</option>
          <option value="price_desc">Price: High to low</option>
        </select>
      </div>

      <label className="field-soft flex items-center gap-3 px-4 py-3 text-sm">
        <input checked={inStock} onChange={(event) => setInStock(event.target.checked)} type="checkbox" />
        In-stock items only
      </label>
    </>
  );

  return (
    <form className="space-y-4 sm:space-y-5" onSubmit={submit}>
      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
          Search products
        </label>
        <input
          className="field-control px-4 py-3 text-sm"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Arduino, relay, ESP32, sensor..."
          value={query}
        />
      </div>

      <div className="md:hidden">
        <details className="rounded-[24px] border border-black/8 bg-white/55 p-4 dark:border-white/10 dark:bg-white/6">
          <summary
            aria-controls={mobileFiltersId}
            className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-nv-ink marker:hidden"
          >
            <span>More filters</span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-nv-slate">Expand</span>
          </summary>
          <div className="mt-4 space-y-5" id={mobileFiltersId}>
            {filterControls}
            <div className="flex gap-3">
              <button className="brand-button flex-1 rounded-full px-4 py-3 text-sm font-semibold text-white transition" type="submit">
                Apply
              </button>
              <button
                className="rounded-full border border-black/10 px-4 py-3 text-sm font-semibold"
                onClick={reset}
                type="button"
              >
                Reset
              </button>
            </div>
          </div>
        </details>
      </div>

      <div className="hidden space-y-5 md:block">
        {filterControls}
        <div className="flex gap-3">
          <button className="brand-button flex-1 rounded-full px-4 py-3 text-sm font-semibold text-white transition" type="submit">
            Apply
          </button>
          <button
            className="rounded-full border border-black/10 px-4 py-3 text-sm font-semibold"
            onClick={reset}
            type="button"
          >
            Reset
          </button>
        </div>
      </div>
    </form>
  );
}
